import { Injectable, Injector } from "@angular/core";
import {
  HttpClient,
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Subject, Observable } from "rxjs";
import { map, first, switchMap, catchError } from "rxjs/operators";

import {
  AUTHENTICATION_SERVICE,
  AuthenticationService,
} from "@core/store/auth/authentication.service";
import { _throw } from "rxjs/observable/throw";
import { parse } from "url";
import { environment } from "@env/environment";
import {
  AUTHORIZATION_SERVICE,
  AuthorizationService,
} from "@core/store/auth/authorization.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Is refresh token is being executed
   */
  private refreshInProgress = false;

  /**
   * Notify all outstanding requests through this subject
   */
  private refreshSubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Only request in the whitelist domain will be processed
   */
  private whitelistedDomains = environment.DOMAIN_WHITELIST;

  /**
   * Routes that should be excluded
   */
  private blacklistedRoutes = [
    environment.API_ENDPOINT + "/login",
    environment.API_ENDPOINT + "/signup",
    environment.API_ENDPOINT + "/password-reset",
    environment.API_ENDPOINT + "/token/refresh",
  ];

  constructor(private injector: Injector) {}

  /**
   * Intercept an outgoing `HttpRequest`
   */
  public intercept(
    req: HttpRequest<any>,
    delegate: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // const authenticationService: AuthenticationService = this.injector.get<AuthenticationService>(AUTH_SERVICE);

    if (!this.isWhitelistedDomain(req) || this.isBlacklistedRoute(req)) {
      // don't process this route
      return delegate.handle(req);
    }

    return this.processIntercept(req, delegate);
  }

  /**
   * Process all the requests via custom interceptors.
   */
  private processIntercept(
    original: HttpRequest<any>,
    delegate: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const clone: HttpRequest<any> = original.clone();

    return this.request(clone).pipe(
      switchMap((req: HttpRequest<any>) => delegate.handle(req)),
      catchError((res: HttpErrorResponse) => this.responseError(clone, res)),
    );
  }

  /**
   * Request interceptor. Delays request if refresh is in progress
   * otherwise adds token to the headers
   */
  private request(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    if (this.refreshInProgress) {
      return this.delayRequest(req);
    }

    return this.addToken(req);
  }

  /**
   * Failed request interceptor, check if it has to be processed with refresh or if subscription ended
   */
  private responseError(
    req: HttpRequest<any>,
    res: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    const authenticationService: AuthenticationService =
      this.injector.get<AuthenticationService>(AUTHENTICATION_SERVICE);
    const authorizationService: AuthorizationService =
      this.injector.get<AuthorizationService>(AUTHORIZATION_SERVICE);

    const refreshShouldHappen: boolean =
      authenticationService.refreshShouldHappen(res);
    const verificationShouldHappen: boolean =
      authorizationService.verificationShouldHappen(res);

    if (refreshShouldHappen && !this.refreshInProgress) {
      this.refreshInProgress = true;

      authenticationService.refreshToken().subscribe(
        () => {
          this.refreshInProgress = false;
          this.refreshSubject.next(true);
        },
        () => {
          this.refreshInProgress = false;
          this.refreshSubject.next(false);
        },
      );
    }

    if (refreshShouldHappen && this.refreshInProgress) {
      return this.retryRequest(req, res);
    }

    if (verificationShouldHappen) {
      console.log("subscription is invalid, do something!");
      authorizationService.verifySubscription();
    }

    return _throw(res);
  }

  /**
   * Add access token to headers or the request
   */
  private addToken(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    const authenticationService: AuthenticationService =
      this.injector.get<AuthenticationService>(AUTHENTICATION_SERVICE);

    return authenticationService.getAccessToken().pipe(
      map((token: string) => {
        if (token) {
          let setHeaders: { [name: string]: string | string[] };
          setHeaders = { Authorization: `Bearer ${token}` };

          return req.clone({ setHeaders });
        }

        return req;
      }),
      first(),
    );
  }

  /**
   * Delay request, by subscribing on refresh event, once it finished, process it
   * otherwise throw error
   */
  private delayRequest(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    return this.refreshSubject.pipe(
      first(),
      switchMap((status: boolean) =>
        status ? this.addToken(req) : _throw(req),
      ),
    );
  }

  /**
   * Retry request, by subscribing on refresh event, once it finished, process it
   * otherwise throw error
   */
  private retryRequest(
    req: HttpRequest<any>,
    res: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    const http: HttpClient = this.injector.get<HttpClient>(HttpClient);

    return this.refreshSubject.pipe(
      first(),
      switchMap((status: boolean) =>
        status ? http.request(req) : _throw(res || req),
      ),
    );
  }

  /**
   * Verify if request should be filtered
   */
  private isWhitelistedDomain(request: HttpRequest<any>): boolean {
    const requestUrl: any = parse(request.url, false, true);

    return (
      requestUrl.host !== null &&
      this.whitelistedDomains.findIndex((domain) =>
        typeof domain === "string"
          ? domain === requestUrl.host
          : <any>domain instanceof RegExp
            ? (<RegExp>domain).test(requestUrl.host)
            : false,
      ) > -1
    );
  }

  /**
   * Verify if request belongs to the blacklisted routes, so in that case we will not process the intercept
   */
  private isBlacklistedRoute(request: HttpRequest<any>): boolean {
    const url = request.url;

    return (
      this.blacklistedRoutes.findIndex((route) =>
        typeof route === "string"
          ? route === url
          : <any>route instanceof RegExp
            ? (<RegExp>route).test(url)
            : false,
      ) > -1
    );
  }
}



// WEBPACK FOOTER //
// /app/src/app/core/services/auth.interceptor.ts